<script lang="ts">
export default {
  name: 'CompaniesAnalysisSurveysDetail',
}
</script>
<script setup lang="ts">
import moment from 'moment'
import { computed, reactive } from 'vue'
import { useStore } from 'vuex'
import { useRoute } from 'vue-router'
import { useI18n } from 'vue-i18n'
import { companiesAnalysisSurveysDetail } from '../../../../stores'
import { toNumberLocaleString } from '../../../../modules/localeString'
import DatePicker from '../../../../components/DatePicker.vue'
import StatusBadge from '../../../../components/StatusBadge.vue'
import Loading from '../../../../components/Loading.vue'
import Modal from '../../../../components/Modal.vue'
import SurveyInputFormPreview from '../../../../components/SurveyInputFormPreview.vue'
import {
  KkTextTypeSurveyAnswerDetail,
  KkRadioTypeSurveyAnswerCountDetail,
  KkCheckboxSurveyAnswerDetail,
  KkNpsDetail,
} from '../../../../domains/analysis'
import type { Period,GaLikeDatePickType } from '../../../../domains/analysis/composables'
import { useGaLikeDatePicker } from '../../../../domains/analysis/composables'
import type {
  SurveysDetailTargetQuestionRadio,
  Answer,
  TargetQuestionNps,
  OverviewSurvey,
  CustomSurvey,
  StatusType,
  CompareType
} from '../../../../domains/analysis/types'
import { KkBox, KkButton } from '../../../../components/base'
import type {
  SurveysDetailData,
  SurveysDetailMetaData,
  MetaQuestion,
} from 'src/biz_spa/stores'

type graphDisplayType = 'count' | 'ratio'


interface State {
  comparePeriodType: { id: GaLikeDatePickType; text: string }[]
  isPreview: boolean
  graphDisplayTypeList: { id: number; type: graphDisplayType }[]
}

const { t } = useI18n()
const route = useRoute()
const store = companiesAnalysisSurveysDetail()
const vuexStore = useStore()
const { changePeriod: changePeriodForGaLikeDatePicker, changeType, type, comparedPeriod, period, compareType, changeCompareType: changeCompareTypeForGaLikeDatePicker }
  = useGaLikeDatePicker({ startDate: new Date(route.query.start_date as string), endDate: new Date(route.query.end_date as string) })

const emit = defineEmits(['update-title', 'update-breadcrumbs'])
const state: State = reactive({
  comparePeriodType: [
    {
      id: 'beforePeriod',
      text: t('pages.companies.analysis.surveys.detail.comparePreviousPeriod'),
    },
    {
      id: 'beforePeriodSameDayOfWeek',
      text: t('pages.companies.analysis.surveys.detail.comparePreviousPeriodSameDayWeek'),
    },
    {
      id: 'beforePeriodLastYear',
      text: t('pages.companies.analysis.surveys.detail.compareSamePeriodPreviousYear'),
    }
  ],
  isPreview: false,
  graphDisplayTypeList: []
})

const surveys = computed<OverviewSurvey[]>(() => vuexStore.state.companiesCustomSurveySurveys.data)
const survey = computed<CustomSurvey>(() => vuexStore.state.companiesCustomSurvey.data)
const customSurveyLoading = computed<boolean>(() => vuexStore.state.companiesCustomSurveySurveys.loading)
const surveyLoading = computed<boolean>(() => vuexStore.state.companiesCustomSurvey.loading)
const companyUuid = computed<string>(() => route.params.company_uuid as string)
const axisType = computed<string>(() => route.query.analysis_type as string)
const axisId = computed<string>(() => route.query.analysis_id as string)
const surveyId = computed<string>(() => route.query.survey_id as string)
const surveysDetailDataList = computed<SurveysDetailData>(() => store.targetPeriodData)
const surveysDetailMetaDataList = computed<SurveysDetailMetaData>(() => store.targetPeriodMetaData)
const surveysDetailComparePeriodDataList = computed<SurveysDetailData>(() => store.comparePeriodData)
const surveysDetailAverageDataList = computed<SurveysDetailData>(() => store.averageData)
const fetchCompareLoading = computed<boolean>(() => store.comparedPeriodLoading)
const fetchAverageLoading = computed<boolean>(() => store.averageDataLoading)
const fetchTargetPeriodLoading = computed<boolean>(() => store.targetPeriodLoading)
const surveysDetailTargetLabel = computed<string>(() => {
  const localLabel = surveysDetailDataList.value.fields
  return compareType.value === 'average' ? localLabel[0].name : dateFormat(period.startDate.value) + ' - ' + dateFormat(period.endDate.value)
})
const surveysDetailCompareLabel = computed<string>(() => {
  const localLabel = surveysDetailAverageDataList.value.fields
  const localCompareLabel = surveyOverview.value?.name
  const localComparePeriod = comparedPeriod.value
  return compareType.value === 'average' ? localCompareLabel + '(' + localLabel[0].name + ')' : dateFormat(localComparePeriod.startDate) + ' - ' + dateFormat(localComparePeriod.endDate)
})
const targetPeriodAnswersCount = computed<number>(() => surveysDetailDataList.value.answersCount)
const comparePeriodAnswersCount = computed<number>(() => surveysDetailComparePeriodDataList.value.answersCount)
const surveyOverview = computed<OverviewSurvey | undefined>(() => surveys.value.find(v => v.id === Number(surveyId.value)))
const surveyName = computed<string>(() => surveyOverview.value?.name ?? '')

/**
 * 数値の3桁区切り
 * @param  { Number } value
 * @return { Number }
 */
const numberLocaleString = (value: number): string => {
  return toNumberLocaleString(value)
}

const extractStatusNumber = (status: StatusType): number => {
  switch (status) {
  case 'published':
    return 1
  case 'unpublished':
    return 2
  case 'closed':
    return 3
  }
}

const dateFormat = (date: Date) => {
  return date ? moment(date).format(t('format.date.lang')) : '-'
}
const queryDateFormat = (date: Date) => {
  return date ? moment(date).format(t('format.date.default')) : '-'
}

const fetchSurveysDetail = async () => {
  const fetch = compareType.value === 'average' ? store.readAnalysisCustomSurveysDetailAverage(
    companyUuid.value,
    {
      answeredAtTerm: queryDateFormat(period.startDate.value) + '-' + queryDateFormat(period.endDate.value)
    },
    {
      id: surveyId.value,
      axisType: axisType.value,
    })
    :
    store.readAnalysisCustomSurveysDetailComparedPeriod(
      companyUuid.value,
      {
        answeredAtTerm: queryDateFormat(comparedPeriod.value.startDate) + '-' + queryDateFormat(comparedPeriod.value.endDate)
      },
      {
        id: surveyId.value,
        axisType: axisType.value,
        axisId: axisId.value
      }
    )
  return Promise.all([
    fetch,
    store.readAnalysisCustomSurveysDetail(
      companyUuid.value,
      {
        answeredAtTerm: queryDateFormat(period.startDate.value) + '-' + queryDateFormat(period.endDate.value)
      },
      {
        id: surveyId.value,
        axisType: axisType.value,
        axisId: axisId.value
      })
  ])
}

const changeComparePeriod = (event: Event): void => {
  const comparePeriodTypeValue = (event.target as HTMLSelectElement).value
  changeType(comparePeriodTypeValue as GaLikeDatePickType)
  fetchSurveysDetail()
}

const changePeriod = (value: Period): void => {
  if (changePeriodForGaLikeDatePicker(value)) {
    fetchSurveysDetail()
  }
}

const changeCompareType = (event: Event) => {
  const compareValue = (event.target as HTMLSelectElement).value
  changeCompareTypeForGaLikeDatePicker(compareValue as CompareType)
  fetchSurveysDetail()
}

const getQuestionTitle = (questions: MetaQuestion): string => {
  return 'Q' + questions.position + '. ' + questions.name
}

const surveysDetailCheckBoxList = (question: MetaQuestion): Answer[] => {
  if (question.type !== 'checkbox') throw new Error('questionのtypeが不正です')
  const questionValues = surveysDetailDataList.value.questions.find((item) => item.id === question.id)
  if (questionValues == null) throw new Error('questionValuesが存在しません')
  if (questionValues.type !== 'checkbox') throw new Error('questionValuesのtypeが不正です')
  const list = compareType.value === 'average' ? surveysDetailAverageDataList : surveysDetailComparePeriodDataList
  const questionCompareValues = list.value.questions.find(v => v.id === question.id)
  if (questionCompareValues == null) throw new Error('questionAverageValuesが存在しません')
  if (questionCompareValues.type !== 'checkbox') throw new Error('questionAverageValuesのtypeが不正です')
  const questionsMetaValues = question.options.map((option) => {
    const targetValue = questionValues.options.find((averageItem) => averageItem.id === option.id)
    if (targetValue == null) throw new Error('targetValueが存在しません')
    const averageValue = questionCompareValues.options.find((item) => item.id === option.id)
    if (averageValue == null) throw new Error('averageValueが存在しません')
    return {
      id: option.id,
      label: option.name,
      value: targetValue.value,
      compareValue: averageValue.value
    }
  })
  return questionsMetaValues
}

const surveysDetailNpsList = (question: MetaQuestion): TargetQuestionNps[] => {
  const npsTargetViewItems = surveysDetailDataList.value.questions.find((targetItem) => targetItem.id === question.id)
  if (npsTargetViewItems == null) throw new Error('npsTargetViewItemsが存在しません')
  if (npsTargetViewItems.type === 'nps') {

    const comparedList = compareType.value === 'average' ? surveysDetailAverageDataList : surveysDetailComparePeriodDataList
    const npsComparedViewItems = comparedList.value.questions.find((targetItem) => targetItem.id === question.id)
    if (npsComparedViewItems == null) throw new Error('npsComparedViewItemsが存在しません')
    if (npsComparedViewItems.type === 'nps') {

      return [
        {
          id: npsTargetViewItems.id,
          name: surveysDetailTargetLabel.value,
          value: npsTargetViewItems.value
        },
        {
          id: npsComparedViewItems.id,
          name: surveysDetailCompareLabel.value,
          value: npsComparedViewItems.value
        }
      ]
    }
  }
  return []
}

const changeDisplayType = (graphDisplayId: number): void => {
  if(state.graphDisplayTypeList.length === 0) return
  const targetIndex = state.graphDisplayTypeList.findIndex((item) => item.id === graphDisplayId)
  state.graphDisplayTypeList[targetIndex].type = state.graphDisplayTypeList[targetIndex].type === 'count' ? 'ratio' : 'count'
}

const getGraphDisplayType = (graphDisplayId: number): graphDisplayType => {
  if(state.graphDisplayTypeList.length === 0) return 'count'
  const targetIndex = state.graphDisplayTypeList.findIndex((item) => item.id === graphDisplayId)
  return state.graphDisplayTypeList[targetIndex].type
}

const surveysDetailRadioList = (question: MetaQuestion): SurveysDetailTargetQuestionRadio[] => {
  const radioTargetViewList = surveysDetailDataList.value.questions.find((radioTargetItem) => radioTargetItem.id === question.id)
  if (radioTargetViewList == null) throw new Error('radioTargetViewListが存在しません')
  const list = compareType.value === 'average' ? surveysDetailAverageDataList : surveysDetailComparePeriodDataList
  const radioComparedViewList = list.value.questions.find((radioComparedItem) => radioComparedItem.id === question.id)
  if (radioComparedViewList == null) throw new Error('radioComparedViewListが存在しません')
  if (radioTargetViewList.type !== 'radio' || radioComparedViewList.type !== 'radio') throw new Error('questionのtypeが不正です')
  return [
    {
      id: 0,
      name: surveysDetailTargetLabel.value,
      questionOptionAnswerCounts: radioTargetViewList.options

    },
    {
      id: 1,
      name: surveysDetailCompareLabel.value,
      questionOptionAnswerCounts: radioComparedViewList.options
    }
  ]
}

const surveysDetailTextAnswerList = (question: MetaQuestion): string[] => {
  const textQuestion = surveysDetailDataList.value.questions.find((targetItem) => targetItem.id === question.id)
  if (textQuestion == null) throw new Error('textQuestionが存在しません')
  if (textQuestion.type !== 'text' && textQuestion.type !== 'one_line_text') throw new Error('questionのtypeが不正です')
  return textQuestion.texts
}

const openPreview = (): void => {
  vuexStore.dispatch('companiesCustomSurvey/readCustomSurvey', surveyId.value)
  state.isPreview = true
}
const closePreview = (): void => {
  state.isPreview = false
}

const load = async () => {
  vuexStore.dispatch('companiesCustomSurveySurveys/readCustomSurveys')
  await fetchSurveysDetail()
  emit('update-title', t('pages.companies.analysis.surveys.detail.title', { name: surveysDetailDataList.value.fields[0].name }))
  state.graphDisplayTypeList = surveysDetailDataList.value.questions.map((item) => { return { id: item.id, type: 'count' } })
}

load()

</script>

<template>
  <div class="p-box">
    <div class="p-box__header">
      <div class="surveysDetailHeader">
        <div class="displaySurveys">
          <span class="u-fwB">
            {{ $t('pages.companies.analysis.surveys.detail.compareSelect') }}：
          </span>
          <div class="c-select">
            <select @change="changeCompareType">
              <option :value="'average'">
                {{ $t('pages.companies.analysis.surveys.detail.compareAverage') }}
              </option>
              <option :value="'period'">
                {{ $t('pages.companies.analysis.surveys.detail.comparePeriod') }}
              </option>
            </select>
          </div>
        </div>
        <KkButton color="secondary" height="46px" :font-bold="false" :text="$t('labels.backToList')"
                  :to="{ name: 'CompaniesAnalysisSurveys', params: { company_uuid: companyUuid } }"
        />
      </div>
      <Modal v-if="state.isPreview" width="375px" @close="closePreview">
        <Loading v-if="surveyLoading" />
        <SurveyInputFormPreview v-else :survey="survey" />
      </Modal>
    </div>

    <div class="p-box__body">
      <section v-if="!customSurveyLoading && surveyOverview != null" class="selectedSurveyInfo">
        <div class="selectedSurveyInfo__title">
          <span>{{ surveyName }}</span>
          <span v-if="surveysDetailDataList.fields.length !== 0">({{ surveysDetailDataList.fields[0].name }})</span>
        </div>
        <div class="selectedSurveyInfo__status">
          <p class="statusMsg">
            <StatusBadge :status="extractStatusNumber(surveyOverview.status)" />
            {{ $t('pages.CompaniesSurveys.publishedStatus.' + surveyOverview.status) }}
          </p>
        </div>
        <div class="selectedSurveyInfo__period">
          <span>{{ $t('labels.date.start') }}：{{ dateFormat(surveyOverview.publishedAt) }}</span>
          <span class="selectedSurveyInfo__period--endDate">
            {{ $t('labels.date.end') }}：{{ dateFormat(surveyOverview.closedAt) }}
          </span>
        </div>
        <div class="selectedSurveyInfo__total">
          <span class="selectedSurveyInfo__title">
            {{
              $t('pages.companies.analysis.surveys.detail.totalNumberResponse', { number: numberLocaleString(surveyOverview.answerCount) })
            }}
          </span>
          <div class="selectedSurveyInfo__preview">
            <a class="selectedSurveyInfo__preview__link" @click.prevent="openPreview">
              {{ $t('pages.CompaniesCustomSurveyAnswers.viewSurveyForm') }}
            </a>
          </div>
        </div>
      </section>
      <Loading v-else />
      <div class="period">
        <div class="period__total">
          <div>
            {{ $t('pages.companies.analysis.surveys.detail.targetPeriodTotalNumberResponse', {
              number:
                numberLocaleString(targetPeriodAnswersCount)
            })
            }}
          </div>
          <div v-if="compareType === 'period'">
            {{ $t('pages.companies.analysis.surveys.detail.beforeTargetPeriodTotalNumberResponse', {
              number:
                numberLocaleString(comparePeriodAnswersCount)
            })
            }}
          </div>
        </div>
        <div class="period__select">
          <div v-if="compareType === 'average'" class="period__select--dateTimePicker">
            <DatePicker :initial-dates="period" opens="left" @input="changePeriod" />
          </div>
          <div v-if="compareType === 'period'">
            <div class="period__select__comparePeriod">
              <DatePicker :initial-dates="period" opens="left" @input="changePeriod" />
              <div class="period__select--versus">
                {{ $t('pages.companies.analysis.surveys.detail.dateVersusPeriod') }}
              </div>
              <div>
                <div class="c-select">
                  <select :value="type" @change="changeComparePeriod">
                    <option v-for="option in state.comparePeriodType" :key="option.id" :value="option.id">
                      {{ option.text }}
                    </option>
                  </select>
                </div>
              </div>
            </div>
            <div class="period__view">
              <p>({{ dateFormat(comparedPeriod.startDate) }} - {{ dateFormat(comparedPeriod.endDate) }})</p>
            </div>
          </div>
        </div>
      </div>
      <template v-if="!fetchTargetPeriodLoading && !fetchAverageLoading && !fetchCompareLoading">
        <section v-for="surveyDetailQuestion in surveysDetailMetaDataList.questions"
                 :key="surveyDetailQuestion.position" class="question__list"
        >
          <KkBox v-if="surveyDetailQuestion.type === 'nps'" :title="getQuestionTitle(surveyDetailQuestion)" border>
            <template #content>
              <KkNpsDetail :comparable-type="compareType"
                           :graph-display-type="getGraphDisplayType(surveyDetailQuestion.id)"
                           :data-list="surveysDetailNpsList(surveyDetailQuestion)"
                           @change-display-type="changeDisplayType(surveyDetailQuestion.id)"
              />
            </template>
          </KkBox>
          <KkBox v-if="surveyDetailQuestion.type === 'checkbox'" :title="getQuestionTitle(surveyDetailQuestion)" border>
            <template #content>
              <KkCheckboxSurveyAnswerDetail :answers="surveysDetailCheckBoxList(surveyDetailQuestion)"
                                            :label="surveysDetailTargetLabel" :compare-label="surveysDetailCompareLabel"
              />
            </template>
          </KkBox>
          <KkBox v-if="surveyDetailQuestion.type === 'text' || surveyDetailQuestion.type === 'one_line_text'" :title="getQuestionTitle(surveyDetailQuestion)" border>
            <template #content>
              <KkTextTypeSurveyAnswerDetail :answers="surveysDetailTextAnswerList(surveyDetailQuestion)" />
            </template>
          </KkBox>
          <KkBox v-if="surveyDetailQuestion.type === 'radio'" :title="getQuestionTitle(surveyDetailQuestion)" border>
            <template #content>
              <KkRadioTypeSurveyAnswerCountDetail
                :graph-display-type="getGraphDisplayType(surveyDetailQuestion.id)"
                :comparable-type="compareType" :data-list="surveysDetailRadioList(surveyDetailQuestion)"
                :question-options="surveyDetailQuestion.options"
                @change-display-type="changeDisplayType(surveyDetailQuestion.id)"
              />
            </template>
          </KkBox>
        </section>
      </template>
      <Loading v-else />
    </div>
  </div>
</template>

<style lang="scss" scoped>
.surveysDetailHeader {
  width: 100%;
  display: flex;
  justify-content: space-between;
  align-items: center;

  @media screen and (max-width: 768px) {
    display: block;
  }
}

.displaySurveys {
  display: flex;
  align-items: baseline;

  @media screen and (max-width: 768px) {
    display: block;
    margin-bottom: 8px;
  }
}

.statusMark {
  border-radius: 50%;
  display: inline-block;
  width: 8px;
  height: 8px;
  margin-right: 2px;
}

.selectedSurveyInfo {
  background: #F5F6F9;
  border: 1px solid #E4E7EB;
  box-shadow: 0px 0px 0px rgba(63, 63, 68, 0.05);
  border-radius: 4px;
  padding: 24px 32px;

  &__title {
    font-weight: 600;
    font-size: 14px;
  }

  &__status {
    padding-top: 8px;
  }

  &__period {
    padding-top: 8px;

    &--endDate {
      padding-left: 8px;
    }
  }

  &__total {
    display: flex;
    padding: 16px 0 0 0;

    @media screen and (max-width: 768px) {
      display: block;
    }
  }

  &__preview {
    padding: 0 0 0 16px;

    @media screen and (max-width: 768px) {
      padding: 0;
    }

    &__link {
      color: #e9446a;
      cursor: pointer;
    }
  }
}

.period {
  display: flex;
  justify-content: space-between;
  align-items: center;
  margin: 40px 0 16px 0;

  @media screen and (max-width: 768px) {
    display: block;
  }

  &__total {
    font-weight: 600;
    font-size: 12px;
  }

  &__select {
    display: flex;
    align-items: center;

    @media screen and (max-width: 768px) {
      display: block;
    }

    &--versus {
      padding: 0 8px;
    }

    &__comparePeriod {
      display: flex;
      align-items: center;
    }

    &--dateTimePicker {
      margin-right: 8px;
    }

    &--comparePeriod {
      margin-left: 8px;

      @media screen and (max-width: 768px) {
        margin-left: 0;
      }
    }
  }

  &__view {
    text-align: right;
  }
}

.question {
  &__list {
    margin: 0 0 30px 0;
  }
}
</style>
